import moment from 'moment';
import { buffers } from 'redux-saga';
import { put, call, takeEvery, select, actionChannel, take, fork } from 'redux-saga/effects';
import * as actions from './actions';

import {
  getId,
  getGuest,
  getGuestId,
  getCabin,
  getVehicle,
  isCabinFull,
  getDeparture,
  getActiveDepartureId,
  scoreCabinBasedOnRemainingBeds,
  isAllGuestsInCabin,
  isGuestsRequiredFieldsFilled,
} from './selectors';
import { changeLanguage, getLanguages } from '../lang/actions';

export const getReservationState = state => state.reservation;

export function* getStateData(selector, id) {
  const state = yield select(getReservationState);
  const reservationId = getId(state);
  const data = selector(state, id);
  return {
    reservationId,
    data,
  };
}

const saveTime = () => {
  const now = moment();
  const saveDay = now.format('L');
  const saveTime = now.format('LT');
  const combindedSaveDayTime = `${saveDay}  ${saveTime}`;
  return combindedSaveDayTime;
};

export function* callApi({
  api,
  resource,
  selector,
  id,
  method = api.put,
  stateData = null,
}) {
  let myStateData = stateData;
  if (myStateData === null) {
    myStateData = yield call(getStateData, selector, id);
  }

  const { reservationId, data } = myStateData;

  const settings = {
    headers: {
      r: reservationId,
    },
  };

  const url = method === api.put ? `/${resource}/${id}` : `/${resource}`;
  let result = null;

  try {
    yield put(actions.savePending(id, resource, 'PENDING', saveTime()));
    result = yield call([api, method], url, data, settings);
    yield put(actions.saveSuccess(id, resource, 'SUCCESS', saveTime()));
  } catch (e) {
    yield put(actions.saveFailed(id, resource, 'FAILED', saveTime()));
  }
  return result;
}

const isAnonymousGuest = guest => guest.isAnonymous;

export function* guestChange(api, { id }) {
  const resource = 'guests';
  const { data: guestId } = yield call(getStateData, getGuestId, id);
  const { data: guestHasRequiredFields } = yield call(getStateData, isGuestsRequiredFieldsFilled, guestId);
  if (guestHasRequiredFields) {
    const stateData = yield call(getStateData, getGuest, guestId);
    const guest = stateData.data;
    const newGuest = yield call(callApi, {
      api,
      resource,
      method: isAnonymousGuest(guest) ? api.post : api.put,
      stateData,
      id: guestId,
    });
    if (isAnonymousGuest(guest)) {
      yield put(actions.replaceGuest(id, newGuest));
    }
  } else {
    yield put(actions.serverSaveFailed('FAILED', saveTime()));
  }
}

export function* cabinChange(api, { id }) {
  yield call(callApi, {
    api,
    resource: 'cabins',
    selector: getCabin,
    id,
  });
}

export function* vehicleChange(api, { id }) {
  yield call(callApi, {
    api,
    resource: 'vehicles',
    selector: getVehicle,
    id,
  });
}

export function* watchAddReservation() {
  const reservation = yield select(getReservationState);
  const pathParts = window.location.pathname.split('/');
  const pathLang = pathParts.length === 4 ? pathParts[3] : null;
  const language = pathLang || reservation.languageCode;
  yield put(changeLanguage(language));
  yield put(getLanguages(reservation.salesOwnerId));
}

export function* watchRequest(api) {
  const requestChan = yield actionChannel(actions.SAVE_REQUEST, buffers.expanding());
  while (true) {
    // 2- take from the channel
    const { payload } = yield take(requestChan);
    // 3- Note that we're using a blocking call
    if (payload.resource === 'guests') {
      yield call(guestChange, api, { id: payload.id });
    }
    if (payload.resource === 'cabins') {
      yield call(cabinChange, api, { id: payload.id });
    }
    if (payload.resource === 'vehicles') {
      yield call(vehicleChange, api, { id: payload.id });
    }
  }
}

const compareCabins = state => (c1, c2) => scoreCabinBasedOnRemainingBeds(state, c2) - scoreCabinBasedOnRemainingBeds(state, c1);
export function* watchCabinStatus(api, { payload: { cabinId } }) {
  const state = yield select(getReservationState);
  if (isCabinFull(state, cabinId)) {
    const departureId = getActiveDepartureId(state);
    const departure = getDeparture(state, departureId);
    if (!isAllGuestsInCabin(state, departureId)) {
      const firstNonFullCabin = departure.cabins.find(id => !isCabinFull(state, id));
      if (firstNonFullCabin) {
        yield put(actions.setActiveCabin(firstNonFullCabin, departureId));
      } else {
        const cabinWithLeastGuests = departure.cabins.map(id => id).sort(compareCabins(state))[0];
        yield put(actions.setActiveCabin(cabinWithLeastGuests, departureId));
      }
    }
  }
}

export default function* ({ api }) {
  yield takeEvery(actions.PUT_GUEST_IN_CABIN, watchCabinStatus, api);
  yield fork(watchRequest, api);
  yield takeEvery(actions.ADD_RESERVATION, watchAddReservation);
}



// WEBPACK FOOTER //
// ./src/store/reservation/sagas.js